import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, ArrowRight, Zap, Lightbulb } from 'lucide-react';

const ComparisonHubSpot = () => {
  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
    window.location.href = homeUrl;
  };

  return (
    <Layout>
      <SEOHead 
        title="NoQ5.ai vs HubSpot Breeze: Voice CRM AI Comparison (2025)"
        description="NoQ5.ai vs HubSpot Breeze: Voice-first CRM updates, GDPR-compliant, €59/user/mo. 7-day free trial, no credit card needed."
        keywords="HubSpot alternative, HubSpot Breeze comparison, HubSpot AI copilot, voice assistant for HubSpot, NoQ5 vs HubSpot, Gong alternative, Clari alternative"
        canonicalUrl="https://noq5.ai/comparison/hubspot"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "NoQ5.ai vs HubSpot Sales Hub & Breeze – Complete Comparison",
          "description": "In-depth comparison of NoQ5.ai and HubSpot for sales teams",
          "author": { "@type": "Organization", "name": "NoQ5.ai" },
          "publisher": { "@type": "Organization", "name": "NoQ5.ai" },
          "datePublished": "2025-10-09"
        }]}
      />
      <div className="container px-4 md:px-6 py-12 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary mb-6">
              CRM AI Comparison
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-foreground">
              NoQ5.ai vs HubSpot Sales Hub & Breeze
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Both help sales teams manage CRM data, but with very different approaches. Here's what you need to know.
            </p>
          </div>

          {/* Quick Verdict */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-primary/5 via-card to-primary/5 border-2 border-primary/20 rounded-2xl p-8 mb-12"
          >
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-foreground">
              <Zap className="w-6 h-6 text-primary" /> Quick Verdict
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              <strong className="text-foreground">Choose HubSpot if:</strong> You need an all-in-one CRM with marketing automation, email campaigns, and a complete sales platform from scratch.
            </p>
            <p className="text-lg text-muted-foreground">
              <strong className="text-foreground">Choose NoQ5.ai if:</strong> You already use HubSpot (or Salesforce) and need a voice-first AI assistant that makes CRM updates instant and effortless with GDPR compliance.
            </p>
          </motion.div>

          {/* Feature Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card rounded-2xl shadow-lg overflow-hidden border border-border mb-12"
          >
            <div className="bg-primary p-6">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-primary-foreground">Feature-by-Feature Comparison</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left py-4 px-6 font-semibold text-foreground">Feature</th>
                    <th className="text-center py-4 px-6 font-semibold text-foreground">HubSpot Sales Hub</th>
                    <th className="text-center py-4 px-6 font-semibold text-primary">NoQ5.ai</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Voice Interface', hubspot: { good: false, text: 'Not available' }, noq5: { good: true, text: 'Full conversational AI' } },
                    { feature: 'Setup Complexity', hubspot: { good: false, text: 'Medium (configuration needed)', warning: true }, noq5: { good: true, text: 'Quick setup (OAuth)' } },
                    { feature: 'Mobile CRM Updates', hubspot: { good: false, text: 'Manual typing required', warning: true }, noq5: { good: true, text: 'Voice-first mobile' } },
                    { feature: 'EU Data Residency', hubspot: { good: false, text: 'US-based (GDPR certified)', warning: true }, noq5: { good: true, text: 'EU-based (Ireland)' } },
                    { feature: 'GDPR Anonymization', hubspot: { good: false, text: 'Not built-in' }, noq5: { good: true, text: 'Automatic' } },
                    { feature: 'Email Marketing', hubspot: { good: true, text: 'Full suite included' }, noq5: { good: false, text: 'Not included (CRM focus)', warning: true } },
                    { feature: 'Marketing Automation', hubspot: { good: true, text: 'Extensive' }, noq5: { good: false, text: 'Not included', warning: true } },
                    { feature: 'CRM Update Speed', hubspot: { good: false, text: 'Manual data entry', warning: true }, noq5: { good: true, text: 'Instant (voice/chat)' } },
                  ].map((row, i) => (
                    <tr key={i} className={`border-b border-border ${i % 2 === 1 ? 'bg-muted/30' : ''}`}>
                      <td className="py-4 px-6 font-medium text-foreground">{row.feature}</td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex flex-col items-center">
                          {row.hubspot.good ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mb-1" />
                          ) : row.hubspot.warning ? (
                            <XCircle className="w-5 h-5 text-amber-500 mb-1" />
                          ) : (
                            <XCircle className="w-5 h-5 text-destructive mb-1" />
                          )}
                          <span className={`text-sm ${row.hubspot.good ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                            {row.hubspot.text}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex flex-col items-center">
                          {row.noq5.good ? (
                            <CheckCircle className="w-5 h-5 text-primary mb-1" />
                          ) : row.noq5.warning ? (
                            <XCircle className="w-5 h-5 text-amber-500 mb-1" />
                          ) : (
                            <XCircle className="w-5 h-5 text-destructive mb-1" />
                          )}
                          <span className={`text-sm ${row.noq5.good ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>
                            {row.noq5.text}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/30">
                    <td className="py-4 px-6 font-medium text-foreground">Focus</td>
                    <td className="py-4 px-6 text-center text-sm text-muted-foreground">Complete CRM platform</td>
                    <td className="py-4 px-6 text-center text-sm font-semibold text-primary">Voice AI assistant layer</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Detailed Breakdown */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-muted/50 rounded-2xl p-8 border border-border"
            >
              <h3 className="text-2xl font-bold mb-6 text-foreground">HubSpot Sales Hub Strengths</h3>
              <ul className="space-y-4">
                {[
                  { title: 'All-in-One Platform', desc: 'CRM, marketing, sales, and service in one place' },
                  { title: 'Email Marketing', desc: 'Comprehensive email campaigns and automation' },
                  { title: 'Free CRM Tier', desc: 'Get started with basic features at no cost' },
                  { title: 'User-Friendly', desc: 'Easier to learn than Salesforce for new users' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-foreground"><strong>{item.title}:</strong> {item.desc}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-primary/5 to-secondary rounded-2xl p-8 border-2 border-primary/20"
            >
              <h3 className="text-2xl font-bold mb-6 text-primary">NoQ5.ai Strengths</h3>
              <ul className="space-y-4">
                {[
                  { title: 'Voice-First', desc: 'Update CRM by speaking naturally, no typing required' },
                  { title: 'Quick Setup', desc: 'Works with existing HubSpot or Salesforce instantly (OAuth token)' },
                  { title: 'EU-Based', desc: 'True EU data residency with automatic GDPR anonymization' },
                  { title: 'Rep-Focused', desc: 'Designed specifically for on-the-go sales reps' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span className="text-foreground"><strong>{item.title}:</strong> {item.desc}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Use Both */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            className="bg-gradient-to-br from-secondary via-card to-accent rounded-2xl p-8 mb-12 border border-border"
          >
            <h3 className="text-2xl font-bold mb-4 flex items-center gap-2 text-foreground">
              <Lightbulb className="w-6 h-6 text-primary" /> Pro Tip: Use Both Together
            </h3>
            <p className="text-lg text-muted-foreground mb-4">
              NoQ5.ai isn't a replacement for HubSpot—it's a voice AI layer that makes HubSpot data entry effortless.
            </p>
            <ul className="space-y-2 text-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">→</span>
                <span>Keep HubSpot for your complete CRM, marketing automation, and email campaigns</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">→</span>
                <span>Add NoQ5.ai so your sales reps can update HubSpot instantly with voice while on the go</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">→</span>
                <span>Result: Better data quality, higher rep adoption, and faster CRM updates</span>
              </li>
            </ul>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-primary rounded-2xl p-8 md:p-12 text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-primary-foreground">Try NoQ5.ai with Your HubSpot</h3>
            <p className="text-lg mb-8 text-primary-foreground/80 max-w-2xl mx-auto">
              Add voice-first CRM updates to your HubSpot in under 5 minutes. No configuration needed.
            </p>
            <Button 
              onClick={scrollToDemo}
              size="lg"
              className="bg-background text-primary hover:bg-background/90 font-semibold px-8 py-6 text-lg rounded-xl"
            >
              Start Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default ComparisonHubSpot;